import { FC, Fragment, useState } from 'react';

import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { ErrorCode } from '@app/models/error';
import Api from '@app/services/api';
import { getResponseErrors, translateApiError } from '@app/services/apiErrors';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { useLocalDispatch } from '@app/services/hooks/useRedux';
import { REFRESH_TOKEN_KEY_NAME, TOKEN_KEY_NAME, TOKEN_KEY_REMEMBER } from '@app/services/httpClient';
import { getAppInitialData } from '@app/store/currentSessionReducer';
import { AuthorizationFormLayout } from '@layouts/AuthorizationFormLayout';

import { Step1 } from './components/Step1';
import { Step2 } from './components/Step2';
import { Step3 } from './components/Step3';
import { Step4 } from './components/Step4';
import { Step5 } from './components/Step5';
import { Step6 } from './components/Step6';
import { INITIAL_FORM_VALUES, INN_STEP } from './constants';
import { IResultFormValues, IStep6 } from './interfaces';
import { isInnError } from './services';

export const Registration: FC = () => {
  const dispatch = useLocalDispatch();
  const navigate = useNavigate();
  const registrationT = useTranslation(NSKeys.registration);
  const commonT = useTranslation(NSKeys.common);
  const validationT = useTranslation(NSKeys.validation);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState<IResultFormValues>(INITIAL_FORM_VALUES);
  const [innError, setInnError] = useState<string>();

  const handleSubmit = async (count: IStep6): Promise<void> => {
    setSubmitting(true);
    const commonFormValues = { ...formValues, ...count };
    try {
      const signInData = await Api.users.postAuthRegister({
        username: commonFormValues.email,
        password: commonFormValues.first_pwd,
        first_name: commonFormValues.name,
        last_name: commonFormValues.surname,
        title: '',
        email: commonFormValues.email,
        inn: commonFormValues.inn,
      });
      localStorage.setItem(REFRESH_TOKEN_KEY_NAME, signInData?.refresh_token);
      localStorage.setItem(TOKEN_KEY_NAME, signInData.access_token);
      localStorage.setItem(TOKEN_KEY_REMEMBER, 'true');

      await Api.users.putScopes({
        settings: { theme: 'light' },
        title: commonFormValues.company_name,
        company: {
          employees_count: commonFormValues.worker_count,
          industry: commonFormValues.sector,
          owner_title: commonFormValues.jobtitle,
        },
      });

      dispatch(getAppInitialData(() => navigate('/dashboard', { replace: true })));
    } catch (err) {
      const errors = getResponseErrors(err);
      const innResponseError = errors.find(isInnError);
      const registryError = errors.find((error) => error.code === ErrorCode.PARTY_REGISTRY_UNAVAILABLE);
      if (innResponseError) {
        setInnError(translateApiError(innResponseError, validationT.t));
        setStep(INN_STEP);
        return;
      }
      if (registryError) {
        message.error(translateApiError(registryError, validationT.t));
        return;
      }
      const { pointerErrors, formMessages } = getFormErrorsFromResponse(
        {
          email: '',
          username: '',
        },
        err,
        validationT.t
      );
      if (pointerErrors.length) {
        setStep(1);
      }
      message.error(
        pointerErrors.length
          ? pointerErrors?.[0]?.errors?.join(', ')
          : formMessages[0] || commonT.t('somethingWentWrong')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = (values: Partial<IResultFormValues>) => {
    setInnError(undefined);
    setFormValues((formValues) => ({ ...formValues, ...values }));
    setStep(step + 1);
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const renderSwitch = (step: number) => {
    switch (step) {
      case 2:
        return (
          <Step2
            onNext={handleNext}
            onPrev={handlePrev}
            initialValues={{ first_pwd: formValues.first_pwd, second_pwd: formValues.second_pwd }}
          />
        );
      case 3:
        return <Step3 onNext={handleNext} onPrev={handlePrev} initialValues={{ sector: formValues.sector }} />;
      case 4:
        return <Step4 onNext={handleNext} onPrev={handlePrev} initialValues={{ jobtitle: formValues.jobtitle }} />;
      case 5:
        return (
          <Step5
            onNext={handleNext}
            onPrev={handlePrev}
            innError={innError}
            initialValues={{ company_name: formValues.company_name, inn: formValues.inn }}
          />
        );
      case 6:
        return (
          <Step6
            onNext={handleSubmit}
            onPrev={handlePrev}
            submitting={submitting}
            initialValues={{ worker_count: formValues.worker_count }}
          />
        );
      default:
        return (
          <Step1
            onNext={handleNext}
            initialValues={{
              surname: formValues.surname,
              name: formValues.name,
              email: formValues.email,
            }}
          />
        );
    }
  };

  return (
    <AuthorizationFormLayout
      title={registrationT.t(`title${step}` as any)}
      description={registrationT.t(`description${step}` as any)}>
      <Fragment>{renderSwitch(step)}</Fragment>
    </AuthorizationFormLayout>
  );
};
